import { FALSE, NULL, TRUE } from "constants/primitives";
import { _DOCUMENT_, _WINDOW_ } from "constants/dom";

import { BULK_SEND_FAILED } from "constants/errors";
import addEventListener from "helpers/dom/addEventListener";
import { bulkLogger } from "helpers/logger";
import createElement from "helpers/dom/createElement";
import { genericObject } from "types/generic";
import removeEventListener from "helpers/dom/removeEventListener";
import report from "helpers/report";
import randomString from "helpers/randomString";
import isString from "helpers/isString";
import includes from "helpers/includes";
import createPromise from "helpers/createPromise";
import getElementById from "helpers/dom/getElementById";

const IFRAME_ID = "bulk-consent";
const TIMEOUT = 9; // timeout in seconds to not wait infinitely for iframe response

/**
 * Creates a new iframe which loads code for cross-origin communication
 * And gives banner ability to read localstorage items from cookiefirst domain
 *
 * @param {string} iframeUrl - Iframe html file url
 * @return {HTMLIFrameElement}
 */
const createIframe = (
  iframeUrl: string,
  loadCallback: () => void
): HTMLIFrameElement => {
  const iframe = createElement("iframe");
  iframe.id = IFRAME_ID;
  iframe.setAttribute("tabindex", "-1");
  iframe.setAttribute("role", "presentation");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "Bulk Consent");

  iframe.style.position = "absolute";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.top = "-9999px";

  iframe.onload = loadCallback;

  iframe.src = iframeUrl;

  // we need to insert the iframe into the <head> because at this point in time,
  // <body> might not be available yet which would cause bulk to fail
  _DOCUMENT_.head.appendChild(iframe);
  return iframe;
};

/**
 * Returns the bulk consent iframe for cross-origin communication
 *
 * @param {string} id - Iframe ID
 * @return {HTMLIFrameElement}
 */
const getIframe = (id: string = ""): HTMLIFrameElement | null => {
  const elem = getElementById(id);
  if (!elem || elem.nodeName !== "IFRAME") {
    return NULL;
  }

  return elem as HTMLIFrameElement;
};

export interface BulkConsentManager {
  sendMessage: <ResponseValue = genericObject>(
    message: genericObject
  ) => Promise<ResponseValue | null>;
}

let __instance: BulkConsentManager | null = null;

export const getBulkConsentManager = (iframeUrl: string) => {
  if (__instance) {
    return __instance;
  }

  let iframe: HTMLIFrameElement | null = getIframe();
  let iframeLoaded: boolean = FALSE;
  const handledMessages: string[] = [];
  if (iframe) {
    iframeLoaded = TRUE;
  }

  __instance = {
    sendMessage<ResponseValue = genericObject>(message: genericObject) {
      return createPromise<ResponseValue | null>((resolve, reject) => {
        // bulk consent won't work in most browsers if iframe is on a different TLD than banner
        // we should include a message for clients about it
        // but so far we let it be, it still works in some browsers
        // if (!includes(_WINDOW_.location.hostname, new URL(iframeUrl).hostname)) {
        //   reject(new Error("Bulk iframe needs to be loaded from the same top level domain."));
        //   return;
        // }
        let timeoutId: NodeJS.Timeout | null = NULL;
        const message_id = `bulk-msg-${randomString()}`;
        if (!message) {
          reject(new Error(BULK_SEND_FAILED));
          return;
        }

        // create iframe when sending first message to it
        // don't create it in constructor as it will be called on all domains
        // and we want iframe to init only on bulk-enabled domains
        if (!iframe) {
          if (!iframeUrl) {
            reject(new Error(BULK_SEND_FAILED));
            return;
          }

          iframe = createIframe(iframeUrl, () => {
            bulkLogger("iframe initialized");
            iframeLoaded = TRUE;
          });
        }
        // handle postMessage response from iframe
        const handleResponse = (
          res: MessageEvent<BulkIframeResponse<ResponseValue>>
        ) => {
          if (!iframe || res.source !== iframe.contentWindow) {
            // res from some other window than our iframe, ignore it
            bulkLogger("ignoring msg from another window", res);
            return;
          }

          try {
            const key = res.message ? "message" : "data";
            let data = res[key];
            if (!data) {
              return;
            }

            if (isString(data)) {
              data = JSON.parse(data) as BulkIframeResponse<ResponseValue>;
            }
            if (
              !data.message_id || // weird message, not sure what to do with it
              data.message_id !== message_id || // response for some other message than the one handled here
              includes(handledMessages, data.message_id) // message aready handled
            ) {
              bulkLogger("ignoring msg:", data);
              return;
            }

            bulkLogger("received response");
            removeEventListener(_WINDOW_, "message", handleResponse, FALSE);

            handledMessages.push(message_id);
            if (timeoutId) {
              clearTimeout(timeoutId);
            }

            if (!data.value) {
              return resolve(NULL);
            }

            if (isString(data.value)) {
              try {
                const val = JSON.parse(data.value);
                return resolve(val);
              } catch {
                return resolve(NULL);
              }
            }

            return resolve(data.value);
          } catch (e) {
            bulkLogger(new Error("failed to read response"));
            report(e);
            return reject();
          }
        };

        if (!iframe || !iframe.contentWindow) {
          bulkLogger("failed to initialize.");
          return reject();
        }

        try {
          // listen for postMessage response from iframe
          addEventListener(_WINDOW_, "message", handleResponse, FALSE);

          // send cookie get request to the iframe
          const payload = JSON.stringify({ ...message, message_id });
          if (iframeLoaded) {
            // send event now, if iframe is already loaded
            iframe.contentWindow.postMessage(payload, "*");
            bulkLogger("send message");
          } else {
            bulkLogger("send message when ready");

            // send event on iframe load
            iframe.onload = () => {
              bulkLogger("iframe initialized");
              const frame = iframe as HTMLIFrameElement;
              if (frame.contentWindow) {
                iframeLoaded = TRUE;
                frame.contentWindow.postMessage(payload, "*");
                bulkLogger("message sent");
              }
            };
          }

          // set timeout of TIMEOUT seconds to not wait indefinitely
          timeoutId = setTimeout(() => {
            bulkLogger(`reached timeout of ${TIMEOUT}s`);
            resolve(NULL);
          }, TIMEOUT * 1000);
        } catch (e) {
          bulkLogger("encountered an error", e);
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          reject(e);
          return;
        }
      });
    },
  };

  return __instance;
};

type BulkIframeResponse<ResponseValue> = {
  message_id: string;
  value: string | ResponseValue;
};

export default BulkConsentManager;
